// Constants
import { URGENCY_TYPES } from '@/models/Transaction'

// Models
import Model from './Model'
import PaymentFee from './PaymentFee'
import { SUBSCRIPTION_PLUS } from '@/models/Subscription'

class Payment extends Model {
  get isExpress () {
    return this.urgency === URGENCY_TYPES.EXPRESS
  }

  get isReturn () {
    return [
      PAYMENT_TAGS.OUTGOING_RETURN,
      PAYMENT_TAGS.INCOMING_RETURN
    ].some(tag => this.tags.includes(tag))
  }

  get isCardLimitsReached () {
    return this.tags.includes(PAYMENT_TAGS.CARD_LIMIT_REACHED)
  }

  get isInsufficientFunds () {
    return this.tags.includes(PAYMENT_TAGS.CARD_NO_FUNDS)
  }

  get isRecurringFee () {
    return this.tags.includes(PAYMENT_TAGS.RECURRING_FEE)
  }

  get isSafeguardingMoneyMovement () {
    return this.tags.includes(PAYMENT_TAGS.SAFEGUARDING_MONEY_MOVEMENT)
  }

  get isCurrencyConversion () {
    return this.tags.includes(PAYMENT_TAGS.CURRENCY_CONVERSION)
  }

  get isReturnRequested () {
    return this.tags.includes(PAYMENT_TAGS.RETURN_REQUESTED)
  }

  get isReturnRequestRejected () {
    return this.tags.includes(PAYMENT_TAGS.RETURN_REQUEST_REJECTED)
  }

  get isVIban () {
    return this.tags.includes(PAYMENT_TAGS.V_IBAN)
  }

  get isSepaInstantReturnRequested () {
    return this.tags.includes(PAYMENT_TAGS.SCT_INST) &&
      this.isReturnRequested
  }

  get isSepaReturnRequested () {
    return this.isSepaTransfer && this.isReturnRequested
  }

  get isSepaInstantTransfer () {
    return this.tags.includes(PAYMENT_TAGS.SCT_INST)
  }

  get isUsLicense () {
    return this.recipientLicense === 'US' || this.senderLicense === 'US'
  }

  get isAutomaticConversion () {
    return this.isCurrencyConversion && this.tags.includes(PAYMENT_TAGS.AUTOMATIC)
  }

  get isInitiated () {
    return this.status === PAYMENT_STATUSES.INITIATED
  }

  get isOnHold () {
    return this.status === PAYMENT_STATUSES.ON_HOLD
  }

  get isPending () {
    return this.status === PAYMENT_STATUSES.PENDING
  }

  get isAwaiting () {
    return this.status === PAYMENT_STATUSES.INITIATED ||
      this.status === PAYMENT_STATUSES.PENDING
  }

  get isProcessing () {
    return this.status === PAYMENT_STATUSES.PROCESSING
  }

  get isProcessingFailed () {
    return this.tags.includes(PAYMENT_TAGS.PROCESSING_FAILED)
  }

  get isCanceled () {
    return this.status === PAYMENT_STATUSES.CANCELED
  }

  get isDone () {
    return this.status === PAYMENT_STATUSES.DONE
  }

  get isPendingCancellation () {
    return this.status === PAYMENT_STATUSES.PENDING_CANCELLATION
  }

  get isOnVerification () {
    return this.tags.includes(PAYMENT_TAGS.VERIFICATION)
  }

  get isInternalSender () {
    return this.senderAccountId != null
  }

  get isInternalRecipient () {
    return this.recipientAccountId != null
  }

  get isInternal () {
    return this.isInternalSender && this.isInternalRecipient
  }

  get isExternal () {
    return this.isWithdrawal || this.isTopup || this.isInvalidTopup
  }

  get isInvalidTopup () {
    return this.senderAccountId == null && this.recipientProfileId == null
  }

  get isWithdrawal () {
    return this.isInternalSender && !this.isInternalRecipient
  }

  get isTopup () {
    return !this.isInternalSender && this.isInternalRecipient
  }

  get isUnidentifiedTopup () {
    return (!this.isInternalSender && this.recipientAccountId == null) || this.hasIdentifyRecipientVerificationTag
  }

  get isSepaTopup () {
    return this.isTopup && this.channel === PAYMENT_CHANNELS.BANK_OF_LITHUANIA
  }

  get isSepaTransfer () {
    return this.hasSepaCreditTransferTag || this.isSepaInstantTransfer
  }

  get isIncomingReturn () {
    return this.tags.includes(PAYMENT_TAGS.INCOMING_RETURN)
  }

  get isOutgoingReturn () {
    return this.tags.includes(PAYMENT_TAGS.OUTGOING_RETURN)
  }

  get isCardReturn () {
    return this.hasReturnTag && this.tags.includes(PAYMENT_TAGS.CARD)
  }

  get isFee () {
    return this.tags.includes(PAYMENT_TAGS.FEE)
  }

  get isWatchdogPassed () {
    return this.watchdogScreeningStatus === WATCHDOG_STATUSES.PASSED
  }

  get isChannelBoL () {
    return this.channel === PAYMENT_CHANNELS.BANK_OF_LITHUANIA
  }

  get isChannelDecta () {
    return this.channel === PAYMENT_CHANNELS.DECTA
  }

  get isChannelMano () {
    return this.channel === PAYMENT_CHANNELS.MANO
  }

  get isChannelIncore () {
    return this.channel === PAYMENT_CHANNELS.INCORE
  }

  get isChannelBilderAccount () {
    return this.channel === PAYMENT_CHANNELS.BILDER_ACCOUNT
  }

  get isScheduled () {
    return this.scheduledPaymentId != null
  }

  get isForced () {
    return this.tags.includes(PAYMENT_TAGS.FORCED)
  }

  get isPostponedSwift () {
    return this.isProcessing && this.hasFinTag && this.hasPostponedTag
  }

  get isRecipientMissingChannel () {
    return this.recipientMissingChannel === true
  }

  get hasOpenBankingIntegrationTag () {
    return this.tags.includes(PAYMENT_TAGS.OPEN_BANKING_INTEGRATION)
  }

  get hasDebt () {
    return this.debt && this.debt.debt === PAYMENT_DEBT.DEBT
  }

  get hasDebtCovered () {
    return this.debt && this.debt.debt === PAYMENT_DEBT.COVERED
  }

  get hasFeeReturn () {
    return this.tags.includes(PAYMENT_TAGS.FEE_RETURN)
  }

  get hasOpenBankingTag () {
    return this.tags.includes(PAYMENT_TAGS.OPEN_BANKING)
  }

  get hasIdentifyRecipientVerificationTag () {
    return this.tags.includes(PAYMENT_TAGS.IDENTIFY_RECIPIENT_VERIFICATION)
  }

  get hasDuplicateTag () {
    return this.tags.includes(PAYMENT_TAGS.DUPLICATE)
  }

  get hasOutgoingReturnTag () {
    return this.tags.includes(PAYMENT_TAGS.OUTGOING_RETURN)
  }

  get hasFinTag () {
    return this.tags.includes(PAYMENT_TAGS.FIN)
  }

  get hasNeedMoreInfoTag () {
    return this.tags.includes(PAYMENT_TAGS.NEED_MORE_INFO)
  }

  get hasAutoForexTag () {
    return this.tags.includes(PAYMENT_TAGS.AUTO_FOREX)
  }
  get hasIntegratorTag () {
    return this.tags.includes(PAYMENT_TAGS.INTEGRATOR)
  }
  get hasIfastApiTag () {
    return this.tags.includes(PAYMENT_TAGS.IFAST_API)
  }
  get hasInterLicenseTag () {
    return this.tags.includes(PAYMENT_TAGS.INTER_LICENSE)
  }

  get hasSpecialRate () {
    return this.tags.includes(PAYMENT_TAGS.SPECIAL_RATE)
  }

  get hasReturnTag () {
    return this.tags.includes(PAYMENT_TAGS.RETURN)
  }

  get hasPostponedTag () {
    return this.tags.includes(PAYMENT_TAGS.POSTPONED)
  }

  get hasAmlAssignee () {
    return this.assignees.aml != null
  }

  get hasComment () {
    return this.comment != null && this.comment !== ''
  }

  get hasAdminComment () {
    return this.adminComment != null && this.adminComment !== ''
  }

  get hasAmlComment () {
    return this.amlComment != null && this.amlComment !== ''
  }

  get hasAccuityStatus () {
    return this.screeningResult != null
  }

  get accuityCanBeResynced () {
    return this.screeningResult === ACCUITY_STATUSES.REJECTED || this.screeningResult === ACCUITY_STATUSES.PASSED
  }

  get hasAccuityFinalStatus () {
    return [ACCUITY_STATUSES.PASSED, ACCUITY_STATUSES.PASSED_BY_INTERNAL_RULES, ACCUITY_STATUSES.REJECTED, ACCUITY_STATUSES.FAILED].includes(this.screeningResult)
  }

  get hasSepaCreditTransferTag () {
    return this.tags.includes(PAYMENT_TAGS.SCT)
  }

  get hasVipStatusPriority () {
    return this.senderProfileVip === true || this.recipientProfileVip === true
  }

  get hasPremiumSubscriptionPriority () {
    return this.senderPremiumSubscription || this.recipientPremiumSubscription
  }

  get senderPremiumSubscription () {
    return this.senderProfileSubscription === SUBSCRIPTION_PLUS
  }

  get recipientPremiumSubscription () {
    return this.recipientProfileSubscription === SUBSCRIPTION_PLUS
  }

  get hasWatchdogResults () {
    return this.watchdogResults != null && this.watchdogResults.length > 0
  }

  get hasWatchdogStatus () {
    return this.watchdogScreeningStatus != null
  }

  get hasAnyScreeningData () {
    return this.hasWatchdogResults || this.hasWatchdogStatus || this.hasAccuityStatus
  }

  get hasFees () {
    return this.fees.length > 0
  }

  get hasClaimNonreceiptReceivedTag () {
    return this.tags.includes(PAYMENT_TAGS.CLAIM_NON_RECEIPT_RECEIVED)
  }

  get hasClaimNonreceiptSentTag () {
    return this.tags.includes(PAYMENT_TAGS.CLAIM_NON_RECEIPT_SENT)
  }

  get hasClaimDateCorrectionReceivedTag () {
    return this.tags.includes(PAYMENT_TAGS.REQ_MODIFY_PAYMENT_RECEIVED)
  }

  get hasClaimDateCorrectionSentTag () {
    return this.tags.includes(PAYMENT_TAGS.REQ_MODIFY_PAYMENT_SENT)
  }

  get hasClaimInvestigationSentTag () {
    return this.tags.includes(PAYMENT_TAGS.RES_INVESTIGATION_SENT)
  }

  get formatters () {
    return {
      fees: dataArray => dataArray.map(data => new PaymentFee(data))
    }
  }

  static get statusOptions () {
    return [
      { name: 'Initiated', value: PAYMENT_STATUSES.INITIATED },
      { name: 'On hold', value: PAYMENT_STATUSES.ON_HOLD },
      { name: 'Pending', value: PAYMENT_STATUSES.PENDING },
      { name: 'Processing', value: PAYMENT_STATUSES.PROCESSING },
      { name: 'Done', value: PAYMENT_STATUSES.DONE },
      { name: 'Canceled', value: PAYMENT_STATUSES.CANCELED }
    ]
  }

  static get operationOptions () {
    return [
      { name: 'Top-up', value: OPERATION_TYPES.TOPUP },
      { name: 'Internal', value: OPERATION_TYPES.INTERNAL },
      { name: 'Withdrawal', value: OPERATION_TYPES.WITHDRAWAL },
      { name: 'Fee', value: OPERATION_TYPES.FEE },
      { name: 'Convert', value: OPERATION_TYPES.CONVERT }
    ]
  }

  static get channelOptions () {
    return [
      { name: 'BluOr Bank (GB)', value: PAYMENT_CHANNELS.BLUOR_BANK, license: 'GB' },
      { name: 'Bank of Lithuania (GB)', value: PAYMENT_CHANNELS.BANK_OF_LITHUANIA, license: 'GB' },
      { name: 'BilderAccount', value: PAYMENT_CHANNELS.BILDER_ACCOUNT, license: undefined },
      { name: 'Bilder (UK)', value: PAYMENT_CHANNELS.BILDER_UK, license: 'GB' },
      { name: 'Alfa Bank (GB)', value: PAYMENT_CHANNELS.ALFA_BANK, license: 'GB' },
      { name: 'ClearBank (GB)', value: PAYMENT_CHANNELS.CLEAR_BANK, license: 'GB' },
      { name: 'Privalgo (GB)', value: PAYMENT_CHANNELS.PRIVALGO, license: 'GB' },
      { name: 'Decta (GB)', value: PAYMENT_CHANNELS.DECTA, license: 'GB' },
      { name: 'ARM (GB)', value: PAYMENT_CHANNELS.ARM_BUSINESS_BANK, license: 'GB' },
      { name: 'CurrencyCloud (GB)', value: PAYMENT_CHANNELS.CURRENCYCLOUD, license: 'GB' },
      { name: 'MuganBank (GB)', value: PAYMENT_CHANNELS.MUGAN_BANK, license: 'GB' },
      { name: 'West (GB)', value: PAYMENT_CHANNELS.WEST, license: 'GB' },
      { name: 'BankingCircle (GB)', value: PAYMENT_CHANNELS.BANKING_CIRCLE, license: 'GB' },
      { name: 'TKB (GB)', value: PAYMENT_CHANNELS.TKB, license: 'GB' },
      { name: 'IFX (GB)', value: PAYMENT_CHANNELS.IFX, license: 'GB' },
      { name: 'Corpay (GB)', value: PAYMENT_CHANNELS.COP, license: 'GB' },
      { name: 'CZCB (GB)', value: PAYMENT_CHANNELS.CZCB, license: 'GB' },
      { name: 'iFast (GB)', value: PAYMENT_CHANNELS.IFAST, license: 'GB' },
      { name: 'Dukascopy (GB)', value: PAYMENT_CHANNELS.DUKASCOPY, license: 'GB' },
      { name: 'Mano Bank (GB)', value: PAYMENT_CHANNELS.MANO, license: 'GB' },
      { name: 'BluOr Bank US (US)', value: PAYMENT_CHANNELS.BO_US, license: 'US' },
      { name: 'Incore Bank AG (GB)', value: PAYMENT_CHANNELS.INCORE, license: 'GB' },
      { name: 'FV Bank (US)', value: PAYMENT_CHANNELS.FV_BANK, license: 'US' }
    ]
  }

  static get forexChannelOptions () {
    return this.channelOptions.filter(
      channelOption => FOREX_PAYMENT_CHANNELS.includes(channelOption.value)
    )
  }

  static get amlTrackingMarkOptions () {
    return [
      { name: 'Request sent', value: AML_TRACKING_MARKS.REQUEST_SENT },
      { name: 'Requested info received', value: AML_TRACKING_MARKS.INFO_RECEIVED },
      { name: 'Not applicable', value: AML_TRACKING_MARKS.NOT_APPLICABLE }
    ]
  }

  get schema () {
    return [
      'id',
      'debt',
      'channel',
      'recipientAccountId',
      'recipientAddress',
      'recipientAmount',
      'recipientBankAccountNumber',
      'recipientBankAddress',
      'recipientBankCountry',
      'recipientBankName',
      'recipientBankSwift',
      'recipientBankBik',
      'recipientBankSortCode',
      'recipientBankAbaNumber',
      'recipientBankVoCode',
      'recipientBankRoutingCode',
      'recipientBankCode',
      'recipientContactNumber',
      'recipientCountry',
      'recipientCity',
      'recipientCurrency',
      'recipientCustomerProfileId',
      'recipientName',
      'recipientOnboarderId',
      'recipientOnboarderName',
      'recipientOnboarderGroup',
      'recipientProfileId',
      'recipientProfileName',
      'recipientProfileStatus',
      'recipientRegNumber',
      'senderAccountId',
      'senderAddress',
      'senderAmount',
      'senderBankAccountNumber',
      'senderBankAddress',
      'senderBankCountry',
      'senderBankName',
      'senderBankSwift',
      'senderCountry',
      'senderCity',
      'senderCurrency',
      'senderCustomerProfileId',
      'senderName',
      'senderOnboarderId',
      'senderOnboarderName',
      'senderOnboarderGroup',
      'senderProfileId',
      'senderProfileName',
      'senderProfileStatus',
      'senderRegNumber',
      'status',
      'tags',
      'type',
      'urgency',
      'amlStatus',
      'adminComment',
      'amlComment',
      'comment',
      'createdAt',
      'updatedAt',
      'finishedAt',
      'fees',
      'currencyRates',
      'docsRequested',
      'screeningResult',
      'accuityCaseId',
      'accuityDatasetId',
      'supervisorAvailableActions',
      'senderProfileVip',
      'recipientProfileVip',
      'assignees',
      'watchdogScreeningStatus',
      'watchdogResults',
      'firstHoldAt',
      'updatedById',
      'paymentOrderId',
      'transferChargeType',
      'paymentPurpose',
      'isCryptoRelated',
      'unidentifiedRecipientComment',
      'identifyRecipientComment',
      'correspondentBankSwift',
      'correspondentBankName',
      'correspondentBankBik',
      'amlTrackingMark',
      'scheduledPaymentId',
      'feeAccountId',
      'vaultAmount',
      'recipientMissingChannel',
      'groupId',
      'senderProfileSubscription',
      'recipientProfileSubscription',
      'recipientLicense',
      'senderLicense',
      'sepaReasons'
    ]
  }
}

export const PAYMENT_TYPES = {
  INTERNAL: 'INTERNAL',
  INTERNAL_WITHIN_PROFILE: 'INTERNAL_WITHIN_PROFILE',
  INTERNAL_TO_SERVICE: 'INTERNAL_TO_SERVICE',
  EXTERNAL: 'EXTERNAL'
}

export const PAYMENT_STATUSES = {
  INITIATED: 'INITIATED',
  ON_HOLD: 'ON_HOLD',
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  PENDING_CANCELLATION: 'PENDING_CANCELLATION',
  CANCELED: 'CANCELED',
  DECLINED: 'DECLINED',
  DONE: 'DONE'
}

export const PAYMENT_TAGS = {
  RETURN: 'RETURN',
  SCT: 'SCT',
  SCT_INST: 'SCT_INST',
  CARD: 'CARD',
  FEE: 'FEE',
  SAFEGUARDING_MONEY_MOVEMENT: 'SAFEGUARDING_MONEY_MOVEMENT',
  AUTOMATIC: 'AUTOMATIC',
  SPECIAL_RATE: 'SPECIAL_RATE',
  RECURRING_FEE: 'RECURRING_FEE',
  VERIFICATION: 'VERIFICATION',
  PROCESSING_FAILED: 'PROCESSING_FAILED',
  CURRENCY_CONVERSION: 'CURRENCY_CONVERSION',
  RETURN_REQUESTED: 'RETURN_REQUESTED',
  RETURN_REQUEST_REJECTED: 'RETURN_REQUEST_REJECTED',
  FEE_RETURN: 'FEE_RETURN',
  INCOMING_RETURN: 'INCOMING_RETURN',
  OUTGOING_RETURN: 'OUTGOING_RETURN',
  OPEN_BANKING: 'OPEN_BANKING',
  IDENTIFY_RECIPIENT_VERIFICATION: 'IDENTIFY_RECIPIENT_VERIFICATION',
  FIN: 'FIN',
  NEED_MORE_INFO: 'NEED_MORE_INFO',
  AUTO_FOREX: 'AUTO_FOREX',
  INTEGRATOR: 'INTEGRATOR',
  INTER_LICENSE: 'INTER_LICENSE',
  POSTPONED: 'POSTPONED',
  FORCED: 'FORCED',
  OPEN_BANKING_INTEGRATION: 'OPEN_BANKING_INTEGRATION',
  DUPLICATE: 'DUPLICATE',
  CLAIM_NON_RECEIPT_RECEIVED: 'CLAIM_NON_RECEIPT_RECEIVED',
  CLAIM_NON_RECEIPT_SENT: 'CLAIM_NON_RECEIPT_SENT',
  REQ_MODIFY_PAYMENT_RECEIVED: 'REQ_MODIFY_PAYMENT_RECEIVED',
  REQ_MODIFY_PAYMENT_SENT: 'REQ_MODIFY_PAYMENT_SENT',
  RES_INVESTIGATION_RECEIVED: 'RES_INVESTIGATION_RECEIVED',
  RES_INVESTIGATION_SENT: 'RES_INVESTIGATION_SENT',
  CARD_LIMIT_REACHED: 'CARD_LIMIT_REACHED',
  CARD_NO_FUNDS: 'CARD_NO_FUNDS',
  E_SIM_FEE: 'E_SIM_FEE',
  V_IBAN: 'V_IBAN',
  IFAST_API: 'IFAST_API'
}

export const ACCUITY_STATUSES = {
  REVIEW: 'REVIEW',
  PROCESSING: 'PROCESSING',
  PASSED: 'PASSED',
  PASSED_BY_INTERNAL_RULES: 'PASSED_BY_INTERNAL_RULES',
  FAILED: 'FAILED',
  REJECTED: 'REJECTED'
}

export const WATCHDOG_STATUSES = {
  PROCESSING: 'PROCESSING',
  MATCHED: 'MATCHED',
  PASSED: 'PASSED',
  FAILED: 'FAILED'
}

export const OPERATION_TYPES = {
  TOPUP: 'TOPUP',
  WITHDRAWAL: 'WITHDRAWAL',
  INTERNAL: 'INTERNAL',
  FEE: 'FEE',
  CONVERT: 'CONVERT'
}

export const OPERATION_GROUPS = {
  ON_HOLD: 'ON_HOLD',
  MANUAL: 'MANUAL',
  VERIFICATION: 'VERIFICATION',
  IDENTIFY_RECIPIENT_VERIFICATION: 'IDENTIFY_RECIPIENT_VERIFICATION',
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  ERRORS: 'ERRORS'
}

export const PAYMENT_CHANNELS = {
  BLUOR_BANK: 'BO',
  BANK_OF_LITHUANIA: 'BANK_OF_LITHUANIA',
  CURRENCYCLOUD: 'CC',
  PRIVALGO: 'PRIVALGO',
  BILDER_ACCOUNT: 'BILDER_ACCOUNT',
  BILDER_UK: 'BILDER_UK',
  CLEAR_BANK: 'CLEAR_BANK',
  ARM_BUSINESS_BANK: 'ARM_BUSINESS_BANK',
  MUGAN_BANK: 'MUGAN_BANK',
  WEST: 'WEST',
  ALFA_BANK: 'ALFA_BANK',
  DECTA: 'DECTA',
  BANKING_CIRCLE: 'BANKING_CIRCLE',
  TKB: 'TKB',
  IFX: 'IFX',
  COP: 'COP',
  CZCB: 'CZCB',
  IFAST: 'IFAST',
  NIUM: 'NIUM',
  DUKASCOPY: 'DUKASCOPY',
  MANO: 'MANO',
  BO_US: 'BO_US',
  INCORE: 'INCORE',
  FV_BANK: 'FV_BANK'
}

export const FOREX_PAYMENT_CHANNELS = [
  PAYMENT_CHANNELS.BLUOR_BANK,
  PAYMENT_CHANNELS.PRIVALGO,
  PAYMENT_CHANNELS.BANKING_CIRCLE,
  PAYMENT_CHANNELS.BILDER_ACCOUNT,
  PAYMENT_CHANNELS.BILDER_UK,
  PAYMENT_CHANNELS.IFAST,
  PAYMENT_CHANNELS.CZCB,
  PAYMENT_CHANNELS.DUKASCOPY,
  PAYMENT_CHANNELS.INCORE,
  PAYMENT_CHANNELS.FV_BANK,
  PAYMENT_CHANNELS.BO_US
]

export const SWIFT_PAYMENT_CHANNELS = [
  PAYMENT_CHANNELS.ALFA_BANK,
  PAYMENT_CHANNELS.BLUOR_BANK,
  PAYMENT_CHANNELS.CLEAR_BANK,
  PAYMENT_CHANNELS.IFX,
  PAYMENT_CHANNELS.COP,
  PAYMENT_CHANNELS.CZCB,
  PAYMENT_CHANNELS.IFAST,
  PAYMENT_CHANNELS.MANO,
  PAYMENT_CHANNELS.PRIVALGO,
  PAYMENT_CHANNELS.BO_US,
  PAYMENT_CHANNELS.INCORE,
  PAYMENT_CHANNELS.FV_BANK
]

export const PAYMENT_DEBT = {
  DEBT: 'DEBT',
  COVERED: 'COVERED',
  NO_DEBT: 'NO_DEBT'
}

export const AML_TRACKING_MARKS = {
  REQUEST_SENT: 'REQUEST_SENT',
  INFO_RECEIVED: 'INFO_RECEIVED',
  NOT_APPLICABLE: 'NOT_APPLICABLE'
}

export const TRANSFER_CHARGE_TYPES = {
  SHA: 'SHA',
  OUR: 'OUR'
}

export default Payment
